import React from 'react';
import COLORS from '../../../../../common/styles/color-pallete';
import { getFileSrcAsBase64 } from '../../../../../utils/library';
import {
  UploadAndCaptureContainer,
  MarginRight,
  OrMessage,
  UploadButtonContainer,
  UploadFileContainer,
  OuterUploadAndCaptureContainerWrapper
} from '../styles';
import { FileUploadTextContainer } from './styles';
import { makeSecondCharLowerCase } from './utils';
import { IUploadFiles } from '../Interfaces/interface';
import {
  NATIONAL_ID,
  SSN_W2_FORM,
  SSN_CARD,
  DRIVING_LICENSE
} from '../../../../../utils/constants';
import { defaultFileSize } from '../../../../nium-forex/utils/constants';
import { PDF_ICON, UPLOAD_BACK_ICON, UPLOAD_FRONT_ICON, UPLOAD_ICON } from '@Images';
import { WebcamCaptureNew } from '@Components';

export const UploadFiles = (props: IUploadFiles): React.ReactElement<IUploadFiles> => {
  const {
    label,
    labelSecondCharLowerFlag,
    fileFrontUploadInputRef,
    fileBackUploadInputRef,
    setFilesError,
    frontFiles,
    backFiles,
    frontFilesBase,
    backFilesBase,
    setFrontFiles,
    setBackFiles,
    setFrontFilesBase,
    setBackFilesBase,
    frontImgFile,
    onFrontImgCaptureChange,
    backImgFile,
    setBackImgFile,
    setBackImgCapture,
    onBackImgCaptureChange,
    fileErrors,
    cmsData,
    selectedRadioFieldName,
    isBackRequired,
    isCaptureButtonRequired,
    countryCode,
    isNiumForexUser,
    fileSizeLimit,
    setFrontImgFile,
    setFrontImgCapture,
    isPOAFlow,
    headingText
  } = props;

  const handleFileChange = (
    selectedFile: React.ChangeEvent<HTMLInputElement>,
    fileType: string,
    setter: (files: FileList | null) => void,
    baseSetter: (data: string) => void
  ) => {
    if (selectedFile?.target?.files?.length) {
      const fileList = selectedFile.target.files;
      setter(fileList);
      if (fileList[0].type.includes('image')) {
        getFileSrcAsBase64(fileList[0]).then((result) => baseSetter(result));
      }
      //@ts-ignore
      if (isNiumForexUser && selectedFile?.target?.files[0].size > fileSizeLimit) {
        //file size will be in bytes. So to convert the size in MB, we need to divide by square of 1024
        const sizeInMB = (fileSizeLimit || defaultFileSize) / (1024 * 1024);
        setFilesError((prevState: any) => ({
          ...prevState,
          [fileType]: `File size should not be more than ${sizeInMB}MB`
        }));
      } else {
        setFilesError((prevState: any) => ({
          ...prevState,
          [fileType]: ''
        }));
      }
    } else {
      setFilesError((prevState: any) => ({
        ...prevState,
        [fileType]: ''
      }));
    }
  };

  const handleFileSelectClick = (isFront: boolean) => {
    if (isFront) {
      if (frontFiles?.length) {
        return null;
      } else {
        fileFrontUploadInputRef?.current?.click();
      }
    } else {
      if (backFiles?.length) {
        return null;
      } else {
        fileBackUploadInputRef?.current?.click();
      }
    }
  };

  const renderFilePreviews = (isFront: boolean, isCaptureButtonRequired?: boolean) => {
    const fileType = isFront ? frontFiles : backFiles;

    return (
      <UploadButtonContainer onClick={() => handleFileSelectClick(isFront)}>
        {fileType?.length ||
        (isCaptureButtonRequired &&
          ((isFront && frontImgFile?.data?.dataURL) ||
            (!isFront && backImgFile?.data?.dataURL))) ? (
          <>
            {fileType?.[0].type === 'application/pdf' ? (
              <>
                <img width={53} height={53} alt={fileType[0].name} src={PDF_ICON} />
                <span className="selected-file-name">{`${fileType[0].name}`}</span>
              </>
            ) : (
              <img
                className="image-file"
                alt={fileType?.[0]?.name || `Captured ${isFront ? 'front' : 'back'} img`}
                src={
                  isFront
                    ? frontFilesBase || frontImgFile?.data?.dataURL
                    : backFilesBase || backImgFile?.data?.dataURL
                }
              />
            )}
          </>
        ) : (
          <>
            {!isBackRequired && (
              <>
                <img src={UPLOAD_ICON} alt="upload-front" />
                <span className="uploadButtons">{'Upload'}</span>
              </>
            )}
            {isBackRequired && (
              <>
                <img src={isFront ? UPLOAD_FRONT_ICON : UPLOAD_BACK_ICON} alt="upload-front" />
                <span className="uploadButtons">{`${
                  isFront ? 'Upload front' : 'Upload back'
                }`}</span>
              </>
            )}
          </>
        )}
      </UploadButtonContainer>
    );
  };

  const getHeading = () => {
    if (headingText) return headingText;
    if (selectedRadioFieldName === SSN_CARD) {
      return `${cmsData?.ssnFormImgHeading} photo`;
    } else if (isPOAFlow && countryCode === 'GB') {
      return `Upload (PDF or image)`;
    } else if (selectedRadioFieldName === SSN_W2_FORM) {
      return cmsData?.w2FormImgHeading;
    } else if (selectedRadioFieldName === DRIVING_LICENSE && countryCode === 'US') {
      return `Upload ${label} photos`;
    } else if (isNiumForexUser) {
      return `${label}`;
    }
    return `${!labelSecondCharLowerFlag ? makeSecondCharLowerCase(label) : label} photos`;
  };

  const getSubHeading = () => {
    if (headingText) return '';
    if (selectedRadioFieldName === SSN_CARD) {
      return cmsData?.ssnUploadSubHeading;
    } else if (selectedRadioFieldName === SSN_W2_FORM) {
      return cmsData?.w2formUploadSubHeading;
    } else if (isPOAFlow && countryCode === 'GB') {
      return `No older than 3 months`;
    }
    return selectedRadioFieldName === NATIONAL_ID
      ? cmsData?.documentUploadInstructionNationalID
      : cmsData?.documentUploadInstruction ||
          `Make sure your details and photo are clearly visible.`;
  };

  const removeFrontUploadedOrCapturedFiles = () => {
    setFrontFiles?.(null);
    setFrontImgFile?.(null);
    setFrontImgCapture?.(null);
    setFrontFilesBase?.(null);
  };
  const removeBackUploadedOrCapturedFiles = () => {
    setBackFiles?.(null);
    setBackImgFile?.(null);
    setBackImgCapture?.(null);
    setBackFilesBase?.(null);
  };

  return (
    <div>
      <FileUploadTextContainer
        headerMarginTop={23}
        textColor={COLORS.black}
        descriptionColor={COLORS.textGrey}
        isPOAFlow={isPOAFlow}
      >
        <span className="__heading">{getHeading()}</span>
        <span className="__description">{getSubHeading()}</span>
        {selectedRadioFieldName === DRIVING_LICENSE && countryCode === 'US' && (
          <span className="__subDescription">
            {cmsData?.documentUploadInstructionDrivingLicense}
          </span>
        )}
      </FileUploadTextContainer>
      <UploadFileContainer>
        {isCaptureButtonRequired ? (
          <OuterUploadAndCaptureContainerWrapper>
            <UploadAndCaptureContainer>
              <MarginRight marginRight={16}>
                <input
                  type="file"
                  id="front-file"
                  ref={fileFrontUploadInputRef}
                  accept=".pdf, .jpg, .jpeg, .png"
                  onChange={(selectedFiles: React.ChangeEvent<HTMLInputElement>) =>
                    handleFileChange(selectedFiles, 'frontError', setFrontFiles, setFrontFilesBase)
                  }
                />
                {renderFilePreviews(true, isCaptureButtonRequired)}
                {<span className="error-msg">{fileErrors.frontError}</span>}
                {(frontFiles || frontImgFile?.data?.dataURL) && (
                  <span className="remove-msg" onClick={removeFrontUploadedOrCapturedFiles}>
                    Remove
                  </span>
                )}
              </MarginRight>

              {!frontFiles && !frontImgFile?.data?.dataURL && (
                <>
                  <OrMessage>Or</OrMessage>
                  <MarginRight marginRight={16}>
                    <WebcamCaptureNew
                      // @ts-ignore
                      onRef={(ref) => ref}
                      fileUploadName={'front_img'}
                      isTakeAPicture={true}
                      isIndependent
                      onChangeFile={onFrontImgCaptureChange}
                      toggleFeature={true}
                      newRevampedtoggleFeature={true}
                      captureMessage={`Capture ${isBackRequired ? 'front' : ''}`}
                      showNewCaptureIcon={true}
                    />
                  </MarginRight>
                </>
              )}
            </UploadAndCaptureContainer>
            {isBackRequired && (
              <UploadAndCaptureContainer>
                <MarginRight marginRight={16}>
                  <input
                    type="file"
                    id="back-file"
                    ref={fileBackUploadInputRef}
                    accept=".pdf, .jpg, .jpeg, .png"
                    onChange={(selectedFiles: React.ChangeEvent<HTMLInputElement>) =>
                      handleFileChange(selectedFiles, 'backError', setBackFiles, setBackFilesBase)
                    }
                  />
                  {renderFilePreviews(false, isCaptureButtonRequired)}
                  <span className="error-msg">{fileErrors.backError}</span>
                  {(backFiles || backImgFile?.data?.dataURL) && (
                    <span className="remove-msg" onClick={removeBackUploadedOrCapturedFiles}>
                      Remove
                    </span>
                  )}
                </MarginRight>

                {!backFiles && !backImgFile?.data?.dataURL && (
                  <>
                    <OrMessage>Or</OrMessage>
                    <MarginRight marginRight={16}>
                      <WebcamCaptureNew
                        // @ts-ignore
                        onRef={(ref) => ref}
                        fileUploadName={'back_img'}
                        isTakeAPicture={true}
                        isIndependent
                        onChangeFile={onBackImgCaptureChange}
                        toggleFeature={true}
                        newRevampedtoggleFeature={true}
                        captureMessage={`Capture ${isBackRequired ? 'back' : ''}`}
                        showNewCaptureIcon={true}
                      />
                    </MarginRight>
                  </>
                )}
              </UploadAndCaptureContainer>
            )}
          </OuterUploadAndCaptureContainerWrapper>
        ) : (
          <>
            <MarginRight marginRight={16}>
              <input
                type="file"
                id="front-file"
                ref={fileFrontUploadInputRef}
                accept=".pdf, .jpg, .jpeg, .png"
                onChange={(selectedFiles: React.ChangeEvent<HTMLInputElement>) =>
                  handleFileChange(selectedFiles, 'frontError', setFrontFiles, setFrontFilesBase)
                }
              />
              {renderFilePreviews(true)}
              {frontFiles?.length && (
                <div className="replace-text-container">
                  <span className="left-text">{`Front`}</span>
                  <span
                    className="replace-text-right"
                    onClick={() => fileFrontUploadInputRef?.current?.click()}
                  >
                    {`Replace`}
                  </span>
                </div>
              )}
              {<span className="error-msg">{fileErrors.frontError}</span>}
            </MarginRight>

            {isBackRequired && (
              <MarginRight marginRight={0}>
                <input
                  type="file"
                  id="back-file"
                  ref={fileBackUploadInputRef}
                  accept=".pdf, .jpg, .jpeg, .png"
                  onChange={(selectedFiles: React.ChangeEvent<HTMLInputElement>) =>
                    handleFileChange(selectedFiles, 'backError', setBackFiles, setBackFilesBase)
                  }
                />
                {renderFilePreviews(false)}
                {backFiles?.length && (
                  <div className="replace-text-container">
                    <span className="left-text">{`Back`}</span>
                    <span
                      className="replace-text-right"
                      onClick={() => fileBackUploadInputRef?.current?.click()}
                    >
                      {`Replace`}
                    </span>
                  </div>
                )}
                {<span className="error-msg">{fileErrors.backError}</span>}
              </MarginRight>
            )}
          </>
        )}
      </UploadFileContainer>
    </div>
  );
};
